import { useQueryClient } from 'react-query';
import { useApiMutation, useApiQuery } from '../../useApi';
import type { GmailResolveChoice } from './gmailResolve';
import {
    AdminHelpArticle,
    ArticleStatus,
    GapStatus,
    HelpArticlePayload,
    HelpArticleAudience,
    InboxAssignee,
    InboxChannel,
    InboxConversation,
    InboxMessage,
    InboxStatus,
    Paginated,
    SupportSettings,
    SupportSettingsUpdate,
    SupportStats,
    SupportSummary,
    UnansweredQuestion,
} from './types';

const BASE = '/super-admin/support';
const ARTICLES = '/super-admin/help-articles';

/** Invalide toutes les requêtes de l'espace support (liste, détail, compteur, articles). */
export function useInvalidateSupport() {
    const qc = useQueryClient();
    return () =>
        qc.invalidateQueries({
            predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0].startsWith(BASE) || q.queryKey[0].startsWith(ARTICLES)),
        });
}

/** Compteur de la navigation : conversations en attente de l'équipe, rafraîchi chaque minute. */
export function useSupportSummary(enabled = true, refetchInterval: number | false = 60_000) {
    return useApiQuery<SupportSummary>(`${BASE}/summary`, {}, { enabled, refetchInterval });
}

/**
 * `status = 'all'` : toutes les conversations (l'API ne filtre pas sans statut) ; `'open'` : toutes sauf les fermées.
 * `assignee = 'all'` : pas de filtre.
 */
export function useSupportInbox(status: InboxStatus | 'open' | 'all', page: number, channel: InboxChannel | 'all' = 'all', assignee: InboxAssignee = 'all', label = '') {
    const params = {
        label: label || undefined,
        status: status === 'all' ? undefined : status,
        page,
        channel: channel === 'all' ? undefined : channel,
        assignee: assignee === 'all' ? undefined : assignee,
    };
    return useApiQuery<Paginated<InboxConversation>>(`${BASE}/conversations`, { params }, { keepPreviousData: true });
}

/** Clé de cache du détail d'une conversation (invalidée par l'aide IA quand un brouillon arrive). */
export const supportConversationUrl = (id: number) => `${BASE}/conversations/${id}`;

/**
 * Détail d'une conversation ouverte, relu toutes les 20 s : l'équipe voit qui la prend,
 * qui est en train de répondre et les nouveaux messages sans recharger.
 */
export function useSupportConversation(id: number | null) {
    return useApiQuery<InboxConversation>(id ? supportConversationUrl(id) : '', {}, { enabled: id !== null, refetchInterval: id !== null ? 20_000 : false });
}

/** `draft_message_id` : brouillon repris (tel quel ou retouché), pour la mesure des brouillons. */
export function useReplySupportConversation() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<InboxMessage, unknown, { id: number; message: string; draft_message_id?: number }>(
        'post',
        ({ id }) => `${BASE}/conversations/${id}/messages`,
        { onSuccess: () => invalidate() },
    );
}

/** Résout la conversation ; `gmail` (e-mail) : rangement choisi dans la fenêtre, sinon dossier retenu ou archivage simple. */
export function useCloseSupportConversation() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<InboxConversation, unknown, { id: number; gmail?: GmailResolveChoice }>('post', ({ id }) => `${BASE}/conversations/${id}/close`, {
        onSuccess: () => invalidate(),
    });
}

/** Mutation de fermeture, transmise à la fenêtre de résolution par l'écran qui la monte. */
export type CloseSupportConversation = ReturnType<typeof useCloseSupportConversation>;

export function useUnansweredQuestions(page: number) {
    return useApiQuery<Paginated<UnansweredQuestion>>(`${BASE}/unanswered`, { params: { status: 'open', page } }, { keepPreviousData: true });
}

export function useUpdateUnansweredQuestion() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<UnansweredQuestion, unknown, { id: number; status: GapStatus }>(
        'patch',
        ({ id }) => `${BASE}/unanswered/${id}`,
        { onSuccess: () => invalidate() },
    );
}

export interface AdminHelpArticleFilters {
    status?: ArticleStatus;
    /** Recherche dans le titre (le serveur coupe à 200 caractères). */
    q?: string;
    /** Identifiant de catégorie, ou `none` pour les articles sans catégorie. */
    categoryId?: number | 'none';
}

export function useAdminHelpArticles(page: number, audience?: HelpArticleAudience, filters: AdminHelpArticleFilters = {}) {
    const q = filters.q?.trim() || undefined;
    return useApiQuery<Paginated<AdminHelpArticle>>(ARTICLES, { params: { page, audience, status: filters.status, q, category_id: filters.categoryId } }, { keepPreviousData: true });
}

export function useAdminHelpArticle(id: number | null) {
    return useApiQuery<AdminHelpArticle>(id ? `${ARTICLES}/${id}` : '', {}, { enabled: id !== null });
}

export function useCreateHelpArticle() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<AdminHelpArticle, unknown, HelpArticlePayload>('post', () => ARTICLES, { onSuccess: () => invalidate() });
}

export function useUpdateHelpArticle() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<AdminHelpArticle, unknown, HelpArticlePayload & { id: number }>('patch', ({ id }) => `${ARTICLES}/${id}`, {
        onSuccess: () => invalidate(),
    });
}

export function useDeleteHelpArticle() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<null, unknown, { id: number }>('delete', ({ id }) => `${ARTICLES}/${id}`, { onSuccess: () => invalidate() });
}

export function useResendSupportMessage() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<InboxMessage, unknown, { id: number }>('post', ({ id }) => `${BASE}/messages/${id}/resend`, {
        onSettled: () => invalidate(),
    });
}

export function useDiscardSupportDraft() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<null, unknown, { id: number }>('post', ({ id }) => `${BASE}/messages/${id}/discard`, { onSuccess: () => invalidate() });
}

export function useSupportSettings() {
    return useApiQuery<SupportSettings>(`${BASE}/settings`);
}

export function useUpdateSupportSettings() {
    const invalidate = useInvalidateSupport();
    return useApiMutation<SupportSettings, unknown, SupportSettingsUpdate>('put', () => `${BASE}/settings`, { onSuccess: () => invalidate() });
}

export function useSupportStats(period: number) {
    return useApiQuery<SupportStats>(`${BASE}/stats`, { params: { period } });
}
